import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { alpha, Box, Button, IconButton, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ExperienceCard from '../../../Experiences/ExperienceCard';
import ExperienceCardLoadingSkeleton from '../../../Experiences/ExperienceCardLoadingSkeleton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useFormContext, useWatch } from 'react-hook-form';

const CARD_WIDTH = 153;
const GAP = 16;
const MIN_SKELETON_MS = 800;

const ExperiencesList = ({
    experiences,
    isLoading,
}: {
    experiences: any[];
    isLoading: boolean;
}) => {
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showSkeleton, setShowSkeleton] = useState(isLoading);
    const skeletonStartedAt = useRef<number>(isLoading ? Date.now() : 0);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const { setValue, control } = useFormContext();

    useEffect(() => {
        clearTimeout(timer.current);

        if (isLoading) {
            skeletonStartedAt.current = Date.now();
            setShowSkeleton(true);
        } else if (showSkeleton) {
            const elapsed = Date.now() - skeletonStartedAt.current;
            const remaining = MIN_SKELETON_MS - elapsed;

            if (remaining > 0) {
                timer.current = setTimeout(() => {
                    setShowSkeleton(false);
                }, remaining);
            } else {
                setShowSkeleton(false);
            }
        }

        return () => clearTimeout(timer.current);
    }, [isLoading]);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButtons = useCallback(() => {
        const element = scrollRef.current;
        if (!element) return;
        setCanScrollLeft(element.scrollLeft > 0);
        setCanScrollRight(
            element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
        );
    }, []);

    useEffect(() => {
        updateScrollButtons();
    }, [experiences, showSkeleton, updateScrollButtons]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;
        const observer = new ResizeObserver(updateScrollButtons);
        observer.observe(element);
        return () => observer.disconnect();
    }, [updateScrollButtons]);

    const scroll = useCallback((direction: 'left' | 'right') => {
        if (!scrollRef.current) return;
        const amount = CARD_WIDTH + GAP;
        scrollRef.current.scrollBy({
            left: direction === 'left' ? -amount : amount,
            behavior: 'smooth',
        });
    }, []);

    const experienceId = useWatch({
        control,
        name: 'itineraryItemInfo.experienceId',
    });

    const handleSelectExperience = (experience: any) => {
        if (experienceId === experience.id) {
            setValue('itineraryItemInfo.experienceId', undefined);
            return;
        }
        setValue('itineraryItemInfo.experienceId', experience.id);
    };

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {canScrollLeft && (
                <IconButton
                    onClick={() => scroll('left')}
                    size='small'
                    sx={{
                        position: 'absolute',
                        left: -16,
                        top: 100,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                    }}
                >
                    <ChevronLeft />
                </IconButton>
            )}

            <Box
                ref={scrollRef}
                onScroll={updateScrollButtons}
                sx={{
                    display: 'flex',
                    gap: `${GAP}px`,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    py: 1,
                    px: 0.5,
                    minHeight: 300,
                }}
            >
                {showSkeleton
                    ? [1, 2, 3, 4, 5].map((item) => (
                          <Box
                              key={item}
                              sx={{
                                  minWidth: CARD_WIDTH,
                                  maxWidth: CARD_WIDTH,
                              }}
                          >
                              <ExperienceCardLoadingSkeleton />
                          </Box>
                      ))
                    : experiences.map((experience: any, index: number) => {
                          const isSelected = experienceId === experience.id;
                          return (
                              <Box
                                  key={experience.id}
                                  sx={{
                                      minWidth: CARD_WIDTH,
                                      maxWidth: CARD_WIDTH,
                                      position: 'relative',
                                      cursor: 'pointer',
                                      borderRadius: '8px',
                                      outline: isSelected
                                          ? `1px solid ${theme.palette.green.main}`
                                          : 'none',
                                  }}
                                  onClick={() =>
                                      handleSelectExperience(experience)
                                  }
                              >
                                  <ExperienceCard
                                      experience={experience}
                                      visitType='business'
                                      isItineraryItemForm={true}
                                  />

                                  {isSelected && (
                                      <Box
                                          sx={{
                                              position: 'absolute',
                                              bottom: 3,
                                              right: 1,
                                              bgcolor: alpha(
                                                  theme.palette.background
                                                      .paper,
                                                  0.7,
                                              ),
                                              borderRadius: '50%',
                                              boxShadow: 1,
                                          }}
                                      >
                                          <CheckCircleOutlineIcon
                                              sx={{ color: 'green.main' }}
                                          />
                                      </Box>
                                  )}
                              </Box>
                          );
                      })}
            </Box>

            {canScrollRight && (
                <IconButton
                    onClick={() => scroll('right')}
                    size='small'
                    sx={{
                        position: 'absolute',
                        right: -16,
                        top: 100,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                    }}
                >
                    <ChevronRight />
                </IconButton>
            )}
        </Box>
    );
};

export default ExperiencesList;
